import { find as lodashFind, set as lodashSet } from 'lodash-es';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { getCurrentFolderFromUrl, isProductionMode } from 'src/js/utils';
import { isSystemFolder } from 'newJs/utils';
import { MAIL } from 'newJs/config';

const MSD = MAIL.get('SYSTEM_DIRS');

const foldersCachingStore = (set) => ({
    invalidateCache: false,
    toggleInvalidateCache: (newState) => set({ invalidateCache: newState }),
});

export const useFoldersCaching = create(
    devtools(foldersCachingStore, {
        enabled: !isProductionMode(),
        name: 'folder-list-caching',
    })
);

const foldersStore = (set) => ({
    foldersList: {},
    recentIndicatorData: false,
    selectedFolder: getCurrentFolderFromUrl(),
    showUserFolders: false,

    setSelectedFolder: (folderName = MSD.INBOX) => {
        // console.log('set selected folder:', folderName);
        return set({ selectedFolder: folderName });
    },
    setRecentIndicatorData: (recentIndicatorData) => set({ recentIndicatorData }),
    updateFoldersList: (newList) => set(
        (state) => {
            // console.log('state', state);
            return {
                foldersList: { ...state.foldersList, ...newList }
            };
        }
    ),
    toggleUserFolders: (val) => set(
        (state) => {
            return { showUserFolders: typeof val === 'boolean' ? val :!state.showUserFolders };
        }
    ),
    updateFolderUnreadBadge: (folderName, count, markSeen) => {
        const foldersListName = isSystemFolder(folderName)
            ? 'systemFolders'
            : 'userFolders';

        return set(
            (state) => {
                const getCurrentFolder = lodashFind(state.foldersList[foldersListName].list, { id: folderName });
                lodashSet(getCurrentFolder, 'unread', markSeen ? getCurrentFolder.unread - count : getCurrentFolder.unread + count);
                return {
                    foldersList: {
                        ...state.foldersList,
                        'systemFolders': {
                            ...state.foldersList.systemFolders,
                            list: [
                                ...state.foldersList.systemFolders.list
                            ]
                        },
                        'userFolders': {
                            ...state.foldersList.userFolders,
                            list: [
                                ...state.foldersList.userFolders.list
                            ]
                        }
                    }
                };
            }
        );
    },
    updateFolderAfterCompose: (folderName, count) => {
        const foldersListName = isSystemFolder(folderName)
            ? 'systemFolders'
            : 'userFolders';

        return set(
            (state) => {
                const getCurrentFolder = lodashFind(state.foldersList[foldersListName].list, { id: folderName });
                lodashSet(getCurrentFolder, 'unread', count);
                return {
                    foldersList: {
                        ...state.foldersList,
                        'systemFolders': {
                            ...state.foldersList.systemFolders,
                            list: [
                                ...state.foldersList.systemFolders.list
                            ]
                        },
                        'userFolders': {
                            ...state.foldersList.userFolders,
                            list: [
                                ...state.foldersList.userFolders.list
                            ]
                        }
                    }
                };
            }
        );
    },
    incrementFolderBadgeCountAfterMove: (currentFolderId, targetFolderId, flags) => {
        const currentFolderGroupName = isSystemFolder(currentFolderId)
            ? 'systemFolders'
            : 'userFolders';
        const targetFolderGroupName = isSystemFolder(targetFolderId)
            ? 'systemFolders'
            : 'userFolders';

        return set(
            (state) => {
                const foldersList = state.foldersList;
                const currentFolderObject = lodashFind(
                    foldersList[currentFolderGroupName].list,
                    { id: currentFolderId }
                );
                const targetFolderObject = lodashFind(
                    foldersList[targetFolderGroupName].list,
                    { id: targetFolderId }
                );

                lodashSet(currentFolderObject, 'unread', currentFolderObject.unread - flags.unread);
                lodashSet(targetFolderObject, 'recent', targetFolderObject.recent + flags.checked);
                lodashSet(targetFolderObject, 'total', targetFolderObject.total + flags.checked);
                lodashSet(targetFolderObject, 'unread', targetFolderObject.unread + flags.unread);

                return {
                    foldersList: {
                        ...foldersList,
                        'systemFolders': {
                            ...foldersList.systemFolders,
                            list: [...foldersList.systemFolders.list]
                        },
                        'userFolders': {
                            ...foldersList.userFolders,
                            recent: !isSystemFolder(targetFolderId)
                                ? foldersList[targetFolderGroupName].recent + flags.checked
                                : foldersList[targetFolderGroupName].recent,
                            list: [...foldersList.userFolders.list]
                        }
                    }
                };
            }
        );
    },
    updateFolderBadgeCountAfterDelete: (trashFolderId, currentFolderId, flags) => {
        const foldersListName = isSystemFolder(currentFolderId) ? 'systemFolders' : 'userFolders';

        return set(
            (state) => {
                const foldersList = state.foldersList;
                const trashFolderObject = lodashFind(
                    foldersList.userFolders.list,
                    { id: trashFolderId }
                );
                const currentFolderObject = lodashFind(
                    foldersList[foldersListName].list,
                    { id: currentFolderId }
                );

                lodashSet(trashFolderObject, 'unread', trashFolderObject.unread + flags.unread);
                lodashSet(trashFolderObject, 'total', trashFolderObject.total + flags.checked);
                lodashSet(currentFolderObject, 'unread', currentFolderObject.unread - flags.unread);
                lodashSet(currentFolderObject, 'total', currentFolderObject.total - flags.checked);

                return {
                    foldersList: {
                        ...foldersList,
                        'systemFolders': {
                            ...foldersList.systemFolders,
                            list: [...foldersList.systemFolders.list]
                        },
                        'userFolders': {
                            ...foldersList.userFolders,
                            recent: foldersList.userFolders.recent,
                            list: [...foldersList.userFolders.list]
                        }
                    }
                };
            }
        );
    },
    clearFolderRecentCount: (folderName, isSystemFolders) => {
        const foldersListName = isSystemFolders ? 'systemFolders' : 'userFolders';
        return set(
            (state) => {
                const targetFolder = lodashFind(state.foldersList[foldersListName].list, { id: folderName });
                lodashSet(targetFolder, 'recent', 0);
                return {
                    foldersList: {
                        ...state.foldersList,
                        'systemFolders': {
                            ...state.foldersList.systemFolders,
                            list: [
                                ...state.foldersList.systemFolders.list
                            ]
                        },
                        'userFolders': {
                            ...state.foldersList.userFolders,
                            recent: !isSystemFolders ? 0 : state.foldersList[foldersListName].recent,
                            list: [
                                ...state.foldersList.userFolders.list
                            ]
                        }
                    }
                };
            }
        );
    }
});

const pesistentStore = persist(foldersStore, { name: 'folders-list' });

export const useFoldersStore = create(
    devtools(pesistentStore, {
        enabled: !isProductionMode(),
        name: 'folders-list',
        trace: true,
    })
);
